import { logger, stats } from '@fiverr-private/obs';
import { triggerSignUp } from '@fiverr-private/identification_core';
import { getContext } from '@fiverr-private/fiverr_context';
import { BUSINESS_TRIGGER_CTA, useSuccessSignCtaEvent } from '@fiverr-private/business_success_sign_modal';
import { ModalType, openFipMigrationModal, shouldDisplayFullMigrationModal } from '@fiverr-private/pro_migration';
import {
    CodeActivationTriggerArgs,
    openCodeActivationModal,
    useShouldOpenUserActivationModal,
} from '@fiverr-private/code_activation_triggers';
import { UseMigrateParams, MigrationModalParamsPayload, SignInModalParams } from './types';

const useMigrate = ({ id, payload, onSuccess }: UseMigrateParams) => {
    const checkShouldMigrateUser = () => shouldDisplayFullMigrationModal();
    const openUserMigration = () => {
        openFipMigrationModal({
            variant: ModalType.SeamlessUpgradeToPro,
            payload: {
                ...payload,
                onSuccess,
            },
        });
    };

    useSuccessSignCtaEvent({
        source: id,
        type: BUSINESS_TRIGGER_CTA.DEFAULT,
        callback: onSuccess,
    });

    return { openUserMigration, checkShouldMigrateUser };
};

const useSignIn = ({
    id,
    onSuccess,
    signInModalParams,
}: {
    id: string;
    onSuccess: () => void;
    signInModalParams: SignInModalParams;
}) => {
    const { businessTriggerCtaType, biEnrichmentParams, source = 'business_sign_in_steps' } = signInModalParams;

    useSuccessSignCtaEvent({
        type: businessTriggerCtaType,
        source: id,
        callback: onSuccess,
    });

    const checkShouldSignIn = () => !getContext().userId;

    const signIn = async () => {
        await triggerSignUp({
            modalOptions: {
                source,
            },
            biEnrichmentParams,
        });
    };

    return { signIn, checkShouldSignIn };
};

const useActivation = (params: CodeActivationTriggerArgs) => {
    const userIsPending = useShouldOpenUserActivationModal();

    const checkShouldActivateUser = async () => userIsPending();
    const activateUser = () => {
        openCodeActivationModal({
            onError: (error) => {
                logger.warn('Failed to lazy load "UserActivationModal"', { error });
                stats.count('user_session.user_activation_modal', 'lazy_import_failed');
                params.onError?.(error);
            },
            ...params,
        });
    };

    return { activateUser, checkShouldActivateUser };
};

export const useBusinessSignInSteps = ({
    id,
    onSuccess,
    signInModalParams,
    activationModalParams,
    migrateModalParams,
    shouldActivate = true,
    shouldMigrate = true,
}: {
    activationModalParams: CodeActivationTriggerArgs;
    migrateModalParams: MigrationModalParamsPayload;
    signInModalParams: SignInModalParams;
    id: string;
    onSuccess: () => void;
    shouldActivate?: boolean;
    shouldMigrate?: boolean;
}) => {
    const { signIn, checkShouldSignIn } = useSignIn({ id, signInModalParams, onSuccess });
    const { openUserMigration, checkShouldMigrateUser } = useMigrate({
        id,
        payload: migrateModalParams,
        onSuccess,
    });
    const { activateUser, checkShouldActivateUser } = useActivation(activationModalParams);

    /**
     * Check If user is signed in, activated or need to migrate
     */
    async function isActivatedBusinessUser() {
        return !checkShouldSignIn() && !checkShouldMigrateUser() && !(await checkShouldActivateUser());
    }

    /**
     * Check If user need any step and do it
     */
    async function signInSteps() {
        if (checkShouldSignIn()) {
            signIn();
        } else if (shouldActivate && (await checkShouldActivateUser())) {
            activateUser();
        } else if (shouldMigrate && checkShouldMigrateUser()) {
            openUserMigration();
        } else {
            onSuccess();
        }
    }

    return { signInSteps, isActivatedBusinessUser };
};
